/**
 * Ad conversion helpers for the console: Google Ads, X, Reddit (and Quora on
 * sign-up).
 *
 * The pixel base snippets live in web/index.html; this only fires events.
 *
 * Everything here is best-effort and must never throw into a caller: these are
 * third-party scripts that ad blockers routinely remove, and a blocked pixel
 * must not take a checkout or an onboarding flow down with it.
 *
 * Every send AND every decision not to send is logged to the browser console
 * under the `[ads]` prefix (filter the console on it):
 *   - console.info  -> an event was handed to a pixel, with the payload
 *   - console.warn  -> an event was NOT sent, with the reason
 *   - console.debug -> routine no-ops that run on every page load (enable the
 *                      "Verbose" level in DevTools to see them)
 */
const GOOGLE_ADS_ID = "AW-749090699";

const LOG_PREFIX = "[ads]";

// Each base snippet in index.html installs a queueing stub under these globals;
// the vendor script swaps in (or attaches) the real sender once it loads. A stub
// with no vendor script behind it accepts events and sends nothing, which is
// what an ad blocker looks like from in here.
const PIXEL_LOADED = {
  gtag: () => !!(window.google_tag_manager || window.google_tag_data),
  twq: () => !!window.twq?.exe,
  rdt: () => !!window.rdt?.sendEvent,
  qp: () => !!window.qp?.qp,
};

/**
 * Whether a pixel can actually send: "loaded", "queued" (stub only; the event
 * leaves if and when the vendor script loads, and never if it is blocked), or
 * "missing" (no global at all).
 *
 * @param {"gtag"|"twq"|"rdt"|"qp"} fnName
 */
export function pixelState(fnName) {
  try {
    if (typeof window[fnName] !== "function") return "missing";
    return PIXEL_LOADED[fnName]?.() ? "loaded" : "queued";
  } catch (e) {
    return "unknown";
  }
}

/** Log an event that was handed to a platform. */
export function logAdSent(platform, event, details = {}) {
  reportAdOutcome(platform, event, details.pixel === "queued" ? "queued" : "sent", details);
  try {
    console.info(`${LOG_PREFIX} ${platform} "${event}" SENT`, details);
  } catch (e) {
    /* logging must never break a caller */
  }
}

/**
 * Log an event that was NOT sent, and why.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.routine] - A no-op that happens on every page load
 *   (nothing pending, already reported). Logged at debug so it doesn't drown
 *   the real skips.
 */
export function logAdSkipped(platform, event, reason, details = {}, { routine = false } = {}) {
  if (!routine) reportAdOutcome(platform, event, "skipped", details, reason);
  try {
    const log = routine ? console.debug : console.warn;
    log(`${LOG_PREFIX} ${platform} "${event}" NOT SENT: ${reason}`, details);
  } catch (e) {
    /* logging must never break a caller */
  }
}

function reportAdOutcome(platform, event, outcome, details, reason) {
  if (!["google", "x"].includes(platform) || event === "user_data") return;
  try {
    const numericValue = typeof details.value === "number" && Number.isFinite(details.value);
    window.posthog?.capture("Ads Monitoring: Ad Conversion Outcome", {
      platform,
      conversion_event: event,
      conversion_id: details.transaction_id || details.conversion_id || details.transactionId || null,
      event_id: details.event_id || details.send_to || details.label || null,
      outcome,
      reason: reason || null,
      value: numericValue ? details.value : null,
      value_provided: numericValue,
      currency: numericValue ? details.currency || "USD" : null,
      pixel_state: details.pixel || null,
      reported_from: "browser",
    });
  } catch (error) {
    console.warn("[ads] PostHog conversion reporting failed", error);
  }
}

// Storage can throw on access (private mode, blocked site data), so the latch
// helpers take a getter and treat any failure as "not latched": one possible
// duplicate beats a dropped conversion, and every platform dedupes on the id.
function isLatched(getStorage, key) {
  try {
    return !!getStorage().getItem(key);
  } catch (e) {
    return false;
  }
}

function latch(getStorage, key) {
  try {
    getStorage().setItem(key, "1");
  } catch (e) {
    /* storage unavailable; the platform's own dedupe still applies */
  }
}

const tabStorage = () => sessionStorage;
const browserStorage = () => localStorage;

/**
 * Fire an X, Reddit or Quora pixel event, logging the outcome either way.
 *
 * @returns {boolean} whether the event was handed to the pixel.
 */
function firePixel({ platform, fnName, event, args, details = {} }) {
  const state = pixelState(fnName);
  if (state === "missing") {
    logAdSkipped(
      platform,
      event,
      `window.${fnName} is not defined (base snippet missing, or stripped by an ad blocker)`,
      details
    );
    return false;
  }
  try {
    window[fnName](...args);
    logAdSent(platform, event, { ...details, pixel: state });
    if (state === "queued") {
      console.warn(
        `${LOG_PREFIX} ${platform} "${event}" is only QUEUED: the ${fnName} vendor script has not loaded. It sends when the script loads, and never if it is blocked.`
      );
    }
    return true;
  } catch (error) {
    logAdSkipped(platform, event, "the pixel threw", { ...details, error });
    return false;
  }
}

export const GOOGLE_LABELS = {
  // "Purchase" conversion action, reported when a paid Stripe checkout completes
  // (Stripe has charged the first invoice), so it carries genuine revenue rather
  // than a $0 trial signal.
  purchase: "fEZfCPqL1uUcEIvvmOUC",

  // "stripe-subscribe" conversion action (valued by STEP_VALUES.trialStarted), fired the
  // moment a free-trial checkout returns ($0 charged, card on file). Distinct
  // from `purchase` above, which fires on real paid checkouts — this is the
  // actual "started a trial" conversion Google bids on.
  trialSignup: "zLCOCIrO_K8aEIvvmOUC",

  // "Sign Up" conversion action, reported on every login by reportSignup()
  // below (Google dedupes on the per-user transaction id).
  accountCreated: "yqV1CK6J8oAYEIvvmOUC",

  // "GitHub Install" conversion action, valued by reportGithubLinked().
  githubLinked: "XbuCCLGCsaEdEIvvmOUC",

  // "Begin checkout" conversion action (valued by STEP_VALUES.beginCheckout), fired by
  // reportBeginCheckout() when a checkout session is created, which is when
  // the API creates the team's Stripe customer.
  beginCheckout: "qPXaCPfqycgcEIvvmOUC",

  // "Book onboarding call" conversion action, fired by reportDemoBooked() when
  // a qualified team books an onboarding call on /start. Valued by the API.
  demoBooked: "FS9KCPmV26sdEIvvmOUC",
};

// Conversion value (USD) reported at each funnel step. Each multiplier only
// touches the step it describes:
//   Sign Up          $2, flat
//   GitHub Install   by repo count x4 for an org; valued by the API
//                    (api/api/helpers/get-ad-conversion-value.js)
//   Begin checkout   $20 x path multiplier
//   Trial started    $40 x path multiplier
//   Demo booked      $100 x team size x4 for an org; valued by the API
//                    (demoConversionValue in api/api/utils/qualify-onboarding.js)
//   Purchase         the amount Stripe charged
export const STEP_VALUES = {
  signup: 2,
  beginCheckout: 20,
  trialStarted: 40,
};

// The path /start recommended: GitHub users are worth the most, then MCP.
// "demo" is GitHub Enterprise. Not answered yet, or "manual", gets the default.
const PATH_MULTIPLIERS = { github: 1, demo: 1, mcp: 0.5 };
const DEFAULT_PATH_MULTIPLIER = 0.25;

/**
 * The team's lead path for valuing a conversion. A team with the GitHub App
 * installed counts as GitHub whatever it answered on /start.
 *
 * @param {object} [user] - The console user, with `team.onboardingQualification`.
 * @returns {string|null}
 */
export function leadPath(user) {
  const qualification = user?.team?.onboardingQualification;
  if (qualification?.signals?.githubInstalled) return "github";
  return qualification?.recommendedPath || null;
}

/** A step's base value scaled by the team's path. */
export function pathValue(base, user) {
  const path = leadPath(user);
  const multiplier = PATH_MULTIPLIERS[path] ?? DEFAULT_PATH_MULTIPLIER;
  return { value: base * multiplier, path, pathMultiplier: multiplier };
}

/**
 * Record a funnel conversion and its value in PostHog as "Ad Conversion", once
 * per step and conversion id from this browser. Captured whether or not an ad
 * pixel got through, so PostHog has the full funnel even when ads are blocked.
 *
 * @param {string} step - signup | githubLinked | beginCheckout | trialStarted |
 *   demoBooked | purchase
 * @param {object} props - `conversionId` and `value`, plus what the value was
 *   based on.
 */
export function reportConversionToPosthog(step, { conversionId, value, currency = "USD", ...props }) {
  try {
    const seenKey = `posthog_ad_conversion:${step}:${conversionId}`;
    if (conversionId && isLatched(browserStorage, seenKey)) return;
    if (!window.posthog?.capture) return;
    window.posthog.capture("Ad Conversion", {
      step,
      conversion_id: conversionId || null,
      value: typeof value === "number" && Number.isFinite(value) ? value : null,
      currency,
      ...props,
    });
    if (conversionId) latch(browserStorage, seenKey);
  } catch (e) {
    console.warn(`${LOG_PREFIX} PostHog "Ad Conversion" ${step} failed`, e);
  }
}

// X pixel event tags. Same values as EVENTS.PURCHASE in
// api/api/services/twitter-conversions.js, which reports the server-side copies.
const X_PURCHASE_EVENT = "tw-ojn2n-rd9wy";
// EVENTS.SUBSCRIPTION_STARTED in twitter-conversions.js.
const X_SUBSCRIPTION_STARTED_EVENT = "tw-ojn2n-qde5v";

// New-campaign X events, valued to match Google. Fired alongside the ones above;
// same ids as the EVENTS in twitter-conversions.js so X dedupes each pair.
const X_EVENTS = {
  githubLinked: "tw-ojn2n-rhkqp", // X type "Sign up"
  beginCheckout: "tw-ojn2n-rhkqt", // X type "Add to cart"
  trialStarted: "tw-ojn2n-rhkqx", // X type "Added payment info"
  purchase: "tw-ojn2n-rhkr1", // X type "Purchase"
  // Same tag as EVENTS.DEMO_BOOKED in twitter-conversions.js (the enterprise
  // lead event until X gets a dedicated one).
  demoBooked: import.meta.env?.VITE_X_DEMO_BOOKED_EVENT || "tw-ojn2n-rhu71",
};

// How long after the server records a purchase the console will still fire the
// browser pixels for it. Covers someone whose trial converts while they're away
// and who comes back within the month; older purchases aren't worth reporting.
const PENDING_PURCHASE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// The last Enhanced Conversions payload handed to gtag. fireGoogleConversion()
// also puts it on the conversion hit itself, so a conversion never depends on
// an earlier `set` having been applied.
let googleUserData = null;

/**
 * Hand gtag the signed-in user's email for Enhanced Conversions.
 *
 * gtag hashes it (SHA-256) before it leaves the browser. `user_data` persists
 * for the page, and fireGoogleConversion() also attaches it to each conversion
 * hit. Called on login and again just before a conversion, because a
 * persisted-state reload restores the user without going through the setUser
 * mutation.
 *
 * @param {object} [user] - The console user (email lives on `profile.email`).
 */
export function setGoogleUserData(user) {
  // Optional by design: Enhanced Conversions only improve match rates. A
  // missing email never blocks a conversion, it just goes out without one.
  const email = user?.profile?.email?.trim().toLowerCase();
  if (!email) {
    googleUserData = null;
    logAdSkipped(
      "google",
      "user_data",
      "no email on the user; conversions still send, without Enhanced Conversions data",
      { hasUser: !!user },
      { routine: true }
    );
    return;
  }
  if (typeof window.gtag !== "function") {
    logAdSkipped("google", "user_data", "window.gtag is not defined", {}, { routine: true });
    return;
  }
  googleUserData = { email };
  try {
    window.gtag("set", "user_data", googleUserData);
    console.debug(`${LOG_PREFIX} google "user_data" set`, { pixel: pixelState("gtag") });
  } catch (e) {
    /* pixel blocked or not loaded */
  }
}

/**
 * Fire one of the new-campaign X events. The X pixel hashes `email_address`
 * itself; it is reused from the Enhanced Conversions email set above.
 *
 * @returns {boolean} whether the event was handed to the pixel.
 */
function fireXEvent({ name, conversionId, value, currency = "USD" }) {
  const params = {
    conversion_id: conversionId,
    ...(typeof value === "number" ? { value, currency } : {}),
  };
  return firePixel({
    platform: "x",
    fnName: "twq",
    event: name,
    args: [
      "event",
      X_EVENTS[name],
      { ...params, ...(googleUserData ? { email_address: googleUserData.email } : {}) },
    ],
    // The email itself stays out of the console log.
    details: { event_id: X_EVENTS[name], ...params, email: !!googleUserData },
  });
}

// Reverse lookup so logs say "accountCreated" rather than an opaque label.
const googleEventName = (label) =>
  Object.keys(GOOGLE_LABELS).find((k) => GOOGLE_LABELS[k] === label) || label;

/**
 * Fire a Google Ads conversion.
 * All actions are suppressed when known GitHub facts show zero accessible
 * repos OR an installation owner account less than 24 hours old. Unknown
 * facts keep the existing send behavior. The age check expires automatically.
 *
 * Google dedupes on `transaction_id` per conversion action, so re-sending the
 * same id is safe and is the default here. That matters: sign-up and
 * begin-checkout use ids that are stable per user / per team, and a client-side
 * "already fired" latch on those silently swallowed every send after the first
 * in a tab, which is why only the per-checkout conversion ever showed up.
 *
 * @param {object} opts
 * @param {string} opts.label - Conversion label (see GOOGLE_LABELS).
 * @param {string} opts.transactionId - Stable id for this conversion; Google's
 *   dedupe key. Required: without it a reload or a revisit re-reports the same
 *   conversion, which is exactly how the old Team.vue pixel over-counted.
 * @param {number} [opts.value] - Revenue in major units (dollars, not cents).
 * @param {string} [opts.currency]
 * @param {Function} [opts.eventCallback] - Called by gtag once the hit is sent.
 * @param {boolean} [opts.oncePerSession] - Also skip a repeat within this tab.
 *   Only for conversions fired off a URL (the checkout return), where a reload
 *   or back-navigation would otherwise re-send the hit.
 * @returns {boolean} whether the conversion was handed to gtag.
 */
export function fireGoogleConversion({
  label,
  transactionId,
  value,
  currency = "USD",
  eventCallback,
  oncePerSession = false,
}) {
  const event = googleEventName(label);

  if (!label) {
    logAdSkipped("google", event || "conversion", "no conversion label configured", {
      transactionId,
      value,
      currency,
    });
    return false;
  }
  if (!transactionId) {
    logAdSkipped("google", event, "no transaction id", { label, value, currency });
    return false;
  }

  const seenKey = `gads_fired:${label}:${transactionId}`;
  // Guard against re-firing on reload/back-navigation. Google dedupes on
  // transaction_id too, but only once the hit lands; this stops the duplicate
  // hit being sent at all.
  if (oncePerSession && isLatched(tabStorage, seenKey)) {
    logAdSkipped(
      "google",
      event,
      `already sent in this tab (sessionStorage "${seenKey}"); remove that key to re-send`,
      { label, transactionId, value, currency }
    );
    return false;
  }

  const pixel = pixelState("gtag");
  if (pixel === "missing") {
    logAdSkipped(
      "google",
      event,
      "window.gtag is not defined (gtag snippet missing from index.html, or stripped by an ad blocker)",
      { label, transactionId, value, currency }
    );
    return false;
  }

  const payload = {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    transaction_id: transactionId,
    ...(typeof value === "number" ? { value, currency } : {}),
  };

  try {
    window.gtag("event", "conversion", {
      ...payload,
      // Enhanced Conversions, on the hit itself as well as via `set`.
      ...(googleUserData ? { user_data: googleUserData } : {}),
      ...(eventCallback ? { event_callback: eventCallback } : {}),
    });
    // The email itself stays out of the console log.
    logAdSent("google", event, { ...payload, enhancedConversions: !!googleUserData, pixel });
    if (!googleUserData) {
      console.warn(
        `${LOG_PREFIX} google "${event}" sent WITHOUT Enhanced Conversions data: no signed-in user email was available`
      );
    }
    if (pixel === "queued") {
      console.warn(
        `${LOG_PREFIX} google "${event}" is only QUEUED in dataLayer: gtag.js has not loaded. It sends when the script loads, and never if it is blocked.`
      );
    }
    if (oncePerSession) latch(tabStorage, seenKey);
    return true;
  } catch (error) {
    logAdSkipped("google", event, "gtag threw", { ...payload, error });
    return false;
  }
}

/**
 * Report a sign-up to Google Ads, Reddit and Quora.
 *
 * Fires on every login, with no new-account check: the platforms dedupe on the
 * per-user conversion ids below, so a returning login re-sends the same id
 * rather than counting a second sign-up. Reddit also gets a server-side
 * copy from api/api/helpers/ensure-user.js with the same conversion ids, so a
 * blocked pixel still counts and an unblocked one is deduped.
 *
 * @param {object} user - The whoami user.
 */
export function reportSignup(user) {
  try {
    if (!user?.id) {
      // The id is every platform's dedupe key. Without it each login would
      // count as a new sign-up, so this is the one thing that is required.
      ["google", "reddit", "quora"].forEach((platform) =>
        logAdSkipped(platform, "sign-up", "no user id to key the conversion on", {
          hasUser: !!user,
        })
      );
      return;
    }

    setGoogleUserData(user);
    fireGoogleConversion({
      label: GOOGLE_LABELS.accountCreated,
      transactionId: `signup-${user.id}`,
      value: STEP_VALUES.signup,
      currency: "USD",
    });
    reportConversionToPosthog("signup", {
      conversionId: `signup-${user.id}`,
      value: STEP_VALUES.signup,
    });

    // Reddit: a blocked pixel is covered by the server-side copy.
    firePixel({
      platform: "reddit",
      fnName: "rdt",
      event: "SignUp",
      args: ["track", "SignUp", { conversionId: `signup-${user.id}` }],
      details: { conversionId: `signup-${user.id}` },
    });

    firePixel({
      platform: "quora",
      fnName: "qp",
      event: "CompleteRegistration",
      args: ["track", "CompleteRegistration"],
    });
  } catch (e) {
    // Never let ad tracking break login.
    logAdSkipped("all", "sign-up", "reportSignup threw", { error: e });
  }
}

// The G2 clickthrough tracking id (the campaign identifier from the G2 snippet).
const G2_TRACKING_ID = "b4d63b26-431a-4723-841c-3b69b496afe5";

/**
 * Report a sign-up to G2 Buyer Intent.
 *
 * Unlike the other pixels there is no base snippet in index.html: the G2 tag
 * loads by appending a <script> to <head> at fire time. It is latched in
 * localStorage per user so a returning login does not re-append it, and keyed
 * on user.id so it only fires once a real account exists.
 *
 * @param {object} user - The whoami user.
 */
export function reportG2Signup(user) {
  try {
    if (!user?.id) {
      logAdSkipped("g2", "sign-up", "no user id to key the pixel on", { hasUser: !!user });
      return;
    }

    const key = `g2_signup_fired:${user.id}`;
    if (isLatched(browserStorage, key)) {
      logAdSkipped("g2", "sign-up", "already fired for this user", { userId: user.id }, { routine: true });
      return;
    }

    const src =
      "https://www.g2.com/external_clickthroughs/tracking.js?c=" +
      G2_TRACKING_ID +
      "&p=" +
      encodeURIComponent(document.location.href) +
      "&e=";

    const script = document.createElement("script");
    script.type = "application/javascript";
    script.async = true;
    script.src = src;
    script.onload = () => logAdSent("g2", "sign-up", { userId: user.id });
    script.onerror = () =>
      logAdSkipped("g2", "sign-up", "the G2 script failed to load (blocked or offline)", {
        userId: user.id,
      });
    document.getElementsByTagName("head")[0].appendChild(script);

    latch(browserStorage, key);
  } catch (e) {
    // Never let ad tracking break login.
    logAdSkipped("g2", "sign-up", "reportG2Signup threw", { error: e });
  }
}

/**
 * Report X's "Subscription Started" when a Stripe checkout returns.
 *
 * Fires on any completed checkout (trial or paid). The server reports the same
 * event from the checkout.session.completed webhook with the same
 * conversion_id, so X dedupes the two.
 *
 * @param {string} sessionId - The Stripe Checkout Session id.
 */
export function reportSubscriptionStarted(sessionId) {
  try {
    if (!sessionId) {
      logAdSkipped("x", "subscription-started", "no checkout session id");
      return;
    }
    const params = { conversion_id: `subscription_started-${sessionId}` };
    firePixel({
      platform: "x",
      fnName: "twq",
      event: "subscription-started",
      args: ["event", X_SUBSCRIPTION_STARTED_EVENT, params],
      details: { event_id: X_SUBSCRIPTION_STARTED_EVENT, ...params },
    });
  } catch (e) {
    /* never let ad tracking break the checkout return */
  }
}

/**
 * Report a free-trial start to Google Ads (trialSignup, valued by path) and X ("Added
 * payment info"). One trial per team, so keyed on the team.
 *
 * @param {object} user - The console user, with `team`.
 * @param {string} sessionId - Fallback key when the team id is missing.
 */
export function reportTrialStarted(user, sessionId) {
  try {
    setGoogleUserData(user);
    const teamId = user?.team?.id;
    const transactionId = teamId ? `trial-${teamId}` : String(sessionId);
    const { value, path, pathMultiplier } = pathValue(STEP_VALUES.trialStarted, user);
    fireGoogleConversion({
      label: GOOGLE_LABELS.trialSignup,
      transactionId,
      value,
      currency: "USD",
      // Keyed off the URL, so a reload of the success page would re-send it.
      oncePerSession: true,
    });
    fireXEvent({ name: "trialStarted", conversionId: transactionId, value });
    reportConversionToPosthog("trialStarted", {
      conversionId: transactionId,
      value,
      path,
      pathMultiplier,
      baseValue: STEP_VALUES.trialStarted,
    });
  } catch (e) {
    logAdSkipped("all", "trialStarted", "reportTrialStarted threw", { error: e });
  }
}

/**
 * Report "begin checkout" to Google Ads, once per team.
 *
 * Called right after create-checkout-session succeeds (which creates the
 * team's Stripe customer if it doesn't have one) and right before the
 * redirect to Stripe. The transaction id is per team, so Google counts one
 * begin-checkout per Stripe customer however many times checkout is opened.
 *
 * Resolves once gtag has sent the hit, or after a short timeout, so the
 * caller can await it before navigating away without the redirect cancelling
 * the request. Never rejects.
 *
 * @param {object} user - The console user, with `team` (object or id).
 * @returns {Promise<void>}
 */
export function reportBeginCheckout(user) {
  return new Promise((resolve) => {
    try {
      // Prefer the team id (one begin-checkout per Stripe customer). The user
      // object is not required: a cached or partial user must not drop the
      // conversion, so fall back to the user id, then to a one-off id that
      // Google cannot dedupe but still counts.
      const team = user?.team;
      const teamId = typeof team === "string" ? team : team?.id;
      let transactionId;
      if (teamId) {
        transactionId = `begin-checkout-${teamId}`;
      } else if (user?.id) {
        transactionId = `begin-checkout-user-${user.id}`;
      } else {
        transactionId = `begin-checkout-anon-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 10)}`;
      }
      if (!teamId) {
        console.warn(
          `${LOG_PREFIX} google "beginCheckout": no team id on the user, falling back to "${transactionId}"`,
          { hasUser: !!user, teamType: typeof team }
        );
      }

      setGoogleUserData(user);
      const startedAt = Date.now();
      let settled = false;
      const done = (how) => {
        if (settled) return;
        settled = true;
        console.debug(
          `${LOG_PREFIX} google "beginCheckout" ${how} after ${Date.now() - startedAt}ms; continuing to Stripe`
        );
        resolve();
      };

      const { value, path, pathMultiplier } = pathValue(STEP_VALUES.beginCheckout, user);
      fireXEvent({ name: "beginCheckout", conversionId: transactionId, value });
      reportConversionToPosthog("beginCheckout", {
        conversionId: transactionId,
        value,
        path,
        pathMultiplier,
        baseValue: STEP_VALUES.beginCheckout,
      });

      const sent = fireGoogleConversion({
        label: GOOGLE_LABELS.beginCheckout,
        transactionId,
        value,
        currency: "USD",
        eventCallback: () => done("confirmed by gtag"),
      });
      if (!sent) {
        done("was not sent");
      } else {
        // gtag never calls back if the hit is blocked mid-flight.
        setTimeout(() => done("got no gtag callback (blocked or slow)"), 1000);
      }
    } catch (e) {
      // Never let ad tracking break checkout.
      logAdSkipped("google", "beginCheckout", "reportBeginCheckout threw", { error: e });
      resolve();
    }
  });
}

/**
 * Report a GitHub App install being linked to the user's team, valued by the
 * API (repo-count bucket, x4 for an org). Keyed per team, so it counts once
 * whenever the link happens, at sign-up or later.
 *
 * @param {object} user - The console user, with `team` (object or id).
 * @param {number} value - Lead value from the API; only sent on a new link.
 */
export function reportGithubLinked(user, value) {
  try {
    // null means no new install was linked. $0 (no repos, or a brand-new
    // GitHub account) still counts as a conversion, just with no value.
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      logAdSkipped("google", "githubLinked", "no value from the API (no new install link)", { value }, { routine: true });
      return;
    }
    const team = user?.team;
    const teamId = typeof team === "string" ? team : team?.id;
    if (!teamId) {
      logAdSkipped("google", "githubLinked", "no team id on the user", { hasUser: !!user });
      return;
    }
    setGoogleUserData(user);
    fireGoogleConversion({
      label: GOOGLE_LABELS.githubLinked,
      transactionId: `github-linked-${teamId}`,
      value: Number(value),
      currency: "USD",
    });
    fireXEvent({
      name: "githubLinked",
      conversionId: `github-linked-${teamId}`,
      value: Number(value),
    });
    reportConversionToPosthog("githubLinked", {
      conversionId: `github-linked-${teamId}`,
      value: Number(value),
      ...(user?.team?.googleAdsEligibility || {}),
    });
  } catch (e) {
    logAdSkipped("google", "githubLinked", "reportGithubLinked threw", { error: e });
  }
}

/**
 * Report a booked onboarding call from /start to Google Ads and X.
 *
 * The API decides the value (org installs and bigger teams are worth more; see
 * demoConversionValue in api/api/utils/qualify-onboarding.js) and the id, which
 * is per team, so a second booking dedupes. X also gets a server-side copy with
 * the same conversion id.
 *
 * @param {object} user - The console user (for Enhanced Conversions email).
 * @param {{ id: string, value: number, currency?: string }} conversion
 */
export function reportDemoBooked(user, conversion) {
  try {
    if (!conversion?.id) {
      logAdSkipped("google", "demoBooked", "no conversion id from the API", {});
      return;
    }
    const value = Number(conversion.value) || undefined;
    setGoogleUserData(user);
    fireGoogleConversion({
      label: GOOGLE_LABELS.demoBooked,
      transactionId: conversion.id,
      value,
      currency: conversion.currency || "USD",
    });
    fireXEvent({ name: "demoBooked", conversionId: conversion.id, value });
    reportConversionToPosthog("demoBooked", {
      conversionId: conversion.id,
      value,
      currency: conversion.currency || "USD",
    });
  } catch (e) {
    logAdSkipped("google", "demoBooked", "reportDemoBooked threw", { error: e });
  }
}

// How each platform is told about a purchase. Each returns whether the event
// was actually handed to its pixel.
const PURCHASE_SENDERS = {
  google: ({ transactionId, value, currency }) =>
    fireGoogleConversion({
      label: GOOGLE_LABELS.purchase,
      transactionId,
      value,
      currency,
    }),
  x: ({ transactionId, xValue }) => {
    const params = {
      conversion_id: `purchase-${transactionId}`,
      ...(typeof xValue === "number" ? { value: xValue, currency: "USD" } : {}),
    };
    return firePixel({
      platform: "x",
      fnName: "twq",
      event: "purchase",
      args: ["event", X_PURCHASE_EVENT, params],
      details: { event_id: X_PURCHASE_EVENT, ...params },
    });
  },
  // New-campaign X Purchase, valued at the amount charged like Google.
  xCharged: ({ transactionId, value, currency }) =>
    fireXEvent({
      name: "purchase",
      conversionId: `purchase-${transactionId}`,
      value,
      currency,
    }),
  reddit: ({ transactionId, value, currency }) => {
    const params = {
      conversionId: transactionId,
      ...(typeof value === "number" ? { value, currency } : {}),
    };
    return firePixel({
      platform: "reddit",
      fnName: "rdt",
      event: "Purchase",
      args: ["track", "Purchase", params],
      details: params,
    });
  },
};

// Report one purchase to one platform, at most once from this browser. The
// latch is per platform and shared by the checkout-return path and
// reportPendingPurchase().
function reportPurchaseTo(platform, purchase) {
  const send = PURCHASE_SENDERS[platform];
  if (!send) {
    logAdSkipped(platform, "purchase", "unknown platform");
    return;
  }

  const seenKey = `ads_purchase_reported:${platform}:${purchase.transactionId}`;
  if (isLatched(browserStorage, seenKey)) {
    // Routine: reportPendingPurchase() re-runs on every whoami.
    logAdSkipped(
      platform,
      "purchase",
      `already reported from this browser (localStorage "${seenKey}"); remove that key to re-send`,
      { transactionId: purchase.transactionId },
      { routine: true }
    );
    return;
  }

  let sent = false;
  try {
    sent = send(purchase);
  } catch (e) {
    // Pixel blocked; the server-side copy still reports.
    logAdSkipped(platform, "purchase", "the send threw", { error: e });
  }

  // Latch only what was actually handed to a pixel. Latching up front (as this
  // used to) meant a purchase that hit a missing pixel was marked reported and
  // never retried on a later load.
  if (sent) latch(browserStorage, seenKey);
}

/**
 * Report a purchase to Google Ads, X and Reddit, once per transaction.
 *
 * Every platform's server-side copy (the invoice.paid webhook) uses the same
 * Stripe Checkout Session id, so a pixel that gets through is deduped against
 * it rather than double-counted.
 *
 * @param {object} opts
 * @param {string} opts.transactionId - The Stripe Checkout Session id.
 * @param {number} [opts.value] - Revenue in major units (dollars).
 * @param {string} [opts.currency]
 * @param {number} [opts.xValue] - X's value, which the server reports as the
 *   plan's LTV rather than the invoice.
 * @param {string[]} [opts.platforms] - Which of "google", "x", "reddit" to
 *   report to. The checkout return leaves X out: it doesn't know the LTV, and a
 *   value-less pixel landing before the server copy would be the one X keeps.
 *   X is picked up by reportPendingPurchase() once the webhook has run.
 */
export function reportPurchase({
  transactionId,
  value,
  currency = "USD",
  xValue,
  platforms = ["google", "x", "xCharged", "reddit"],
}) {
  try {
    if (!transactionId) {
      platforms.forEach((platform) =>
        logAdSkipped(platform, "purchase", "no transaction id (Stripe Checkout Session id)")
      );
      return;
    }
    platforms.forEach((platform) =>
      reportPurchaseTo(platform, { transactionId, value, currency, xValue })
    );
    reportConversionToPosthog("purchase", { conversionId: transactionId, value, currency });
  } catch (e) {
    // Never let ad tracking break the caller.
    logAdSkipped("all", "purchase", "reportPurchase threw", { error: e });
  }
}

/**
 * Report a purchase the server recorded but no browser has reported yet.
 *
 * A free trial converts to paid on day 14 with nobody on the page, so the
 * checkout-return pixels never fire for it. The webhook stamps the conversion
 * on the team (`purchaseConversion`); this fires the pixels the next time the
 * owner loads the console. Called from the store's setUser, so it runs on every
 * whoami; reportPurchase() makes the repeats no-ops.
 *
 * @param {object} user - The whoami user, with `team` populated.
 */
export function reportPendingPurchase(user) {
  try {
    const team = user?.team;
    const conversion = team?.purchaseConversion;
    // Runs on every whoami, and for most users there is nothing pending, so
    // these are routine (debug-level) rather than warnings.
    const skip = (reason, details) =>
      logAdSkipped("all", "pending-purchase", reason, details, { routine: true });

    if (user?.role !== "owner") {
      skip("user is not the team owner", { role: user?.role ?? null });
      return;
    }
    if (!conversion?.id || !team.purchaseReportedAt) {
      skip("the server has not recorded a purchase for this team", {
        hasPurchaseConversion: !!conversion?.id,
        purchaseReportedAt: team?.purchaseReportedAt ?? null,
      });
      return;
    }
    const reportedAt = new Date(team.purchaseReportedAt).getTime();
    if (!(Date.now() - reportedAt < PENDING_PURCHASE_WINDOW_MS)) {
      skip("the purchase is older than the 30-day reporting window", {
        purchaseReportedAt: team.purchaseReportedAt,
      });
      return;
    }

    const cents = Number(conversion.valueCents);
    setGoogleUserData(user);
    reportPurchase({
      transactionId: conversion.id,
      value: Number.isFinite(cents) && cents > 0 ? cents / 100 : undefined,
      currency: conversion.currency || "USD",
      xValue: typeof conversion.ltvUsd === "number" ? conversion.ltvUsd : undefined,
    });
  } catch (e) {
    // Never let ad tracking break a user refresh.
    logAdSkipped("all", "pending-purchase", "reportPendingPurchase threw", { error: e });
  }
}

export default {
  fireGoogleConversion,
  logAdSent,
  logAdSkipped,
  pixelState,
  reportBeginCheckout,
  reportG2Signup,
  reportGithubLinked,
  reportPendingPurchase,
  reportPurchase,
  reportSignup,
  reportSubscriptionStarted,
  reportTrialStarted,
  setGoogleUserData,
  GOOGLE_LABELS,
};
